<template>
  <div class="main-container">
           
    <h1>{{ $t("sites.title") }}</h1>
    <div class="mt-2 d-flex align-items-center">
      <button class="btn btn-secondary" :disabled="!isAllowedToCreate" @click="add">
        <b-icon icon="plus" scale="1.2" class="mr-1" /> {{ $t('sites.add') }}
      </button>
      <button class="btn btn-primary ml-auto d-flex align-items-center" :disabled="!modifiedSites.length" @click="save">
  {{ $t('sites.save') }}
      </button>
    </div>
    <!-- Pilot migrated to headless DataTable -->
    <DataTable
      ref="table"
      :columns="dtColumns"
      :url="url"
      trackBy="id"
      :perPage="20"
      :rowClass="rowClassFn"
      @row-clicked="onRowClick"
    >
      <template #editable-name="{ row }">
        <input
          class="form-control form-control-sm"
          v-model="row.translation.name"
          @input="markModified(row)"
          :placeholder="$t('sites.name').toString()"
        />
      </template>
      <template #dropdown-country="{ row }">
        <div class="custom-actions">
          <div
            v-if="!editingCountry[row.translation.id || row.site.id]"
            @click="beginEditCountry(row)"
          >
            {{ displayCountryText(row) }}
          </div>
          <select
            v-else
            :ref="'countrySel-'+(row.translation.id || row.site.id)"
            class="form-control form-control-sm"
            v-model="row.site.country.id"
            @change="handleCountrySelectChange(row)"
            @blur="endEditCountry(row)"
          >
            <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
      </template>
      <template #switch-enabled="{ row }">
        <button
          type="button"
          class="btn btn-switch toggle-switch"
          :class="row.site.isEnabled ? 'active-last' : 'active-first'"
          @click="toggleEnabled(row)"
        >
          <span class="switch-toggler" />
        </button>
      </template>
      <template #editable-idsap="{ row }">
        <input
          class="form-control form-control-sm"
          v-model="row.site.idSap"
          @input="markModified(row)"
          :placeholder="$t('sites.idSap').toString()"
        />
      </template>
      <template #actions="{ row }">
        <button class="btn btn-sm btn-secondary" @click="duplicateRow(row)">{{$t('common.duplicate')}}</button>
      </template>
    </DataTable>
    <div class="mt-2 d-flex align-items-center">
      <button class="btn btn-secondary" :disabled="!isAllowedToCreate" @click="add">
        <b-icon icon="plus" scale="1.2" class="mr-1" /> {{ $t('sites.add') }}
      </button>
      <button class="btn btn-primary ml-auto d-flex align-items-center" :disabled="!modifiedSites.length" @click="save">
        {{ $t('sites.save') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import DataTable from '@/components/Shared/Table/DataTable.vue';
import Site from '@/models/Site';
import SitesServices from '@/services/SitesServices';
import CountryService from '@/services/CountryService';
import { Guid } from 'guid-typescript';
import { UserRoleEnum } from '@/utils/Enums';
import Constants from '@/utils/Constants';

export default Vue.extend({
  name: 'Sites',
  components: { DataTable },
  data() {
    return {
      showModal: false as boolean,
      modifiedSites: [] as Array<any>,
      url: SitesServices.getTableUrlByAdministrator(this.$i18n.locale) as string,
      countries: [] as Array<any>,
      user: {} as any,
      selectedRow: [] as string[],
      editingCountry: {} as Record<string, boolean>,
    };
  },
  computed: {
    dtColumns(): Array<any> {
      return [
        { key: 'translation.name', label: this.$t('sites.name').toString(), slot: 'editable-name' },
        { key: 'site.country.id', label: this.$t('sites.country').toString(), slot: 'dropdown-country' },
        { key: 'site.idSap', label: this.$t('sites.idSap').toString(), slot: 'editable-idsap' },
        { key: 'site.isEnabled', label: this.$t('sites.actifs').toString(), slot: 'switch-enabled' },
      ];
    },
    isAllowedToCreate(): boolean {
      return this.$store.getters.isAllowed([
        UserRoleEnum.countryAdministrator,
        UserRoleEnum.centralAdministrator,
      ]);
    },
  },
  watch: {
    '$i18n.locale'(newLocale: string) {
      this.url = SitesServices.getTableUrlByAdministrator(newLocale);
      this.loadElements();
    }
  },
  methods: {
    refresh(): void {
      this.modifiedSites = [];
      this.forceReload();
      // Clear selected rows after refresh
      (this as any).selectedRow = [];
       this.loadElements();
    },
    forceReload(): void {
      const currentUrl = this.url;
      this.url = '';
      this.$nextTick(() => { this.url = currentUrl; });
    },
    close(): void {
      this.showModal = false;
      this.refresh();
    },
    add(): void {
      const newSite = {
        site: new Site(),
        translation: {
          id: Guid.create().toString(),
          language: { code: 'fr' },
          name: '',
        },
      };
      const tableComp: any = this.$refs.table;
      if (tableComp && tableComp.api) {
        tableComp.api.setRows([newSite, ...tableComp.api.rows]);
      }
      this.markModified(newSite);
      const id = (newSite as any).site?.id || (newSite as any).translation?.id;
      const sel = (this as any).selectedRow; if (id && !sel.includes(id)) sel.push(id);
    },
    async save() {
      for (let modifiedSite of this.modifiedSites) {
        if ((modifiedSite as any).translation.name === '') {
          this.sendNotification(
            this.$t('sites.updateFailed').toString(),
            this.$t('sites.propertyNameEmpty').toString(),
            'error'
          );
          return;
        } else if ((modifiedSite as any).site.country.id === '') {
          this.sendNotification(
            this.$t('sites.updateFailed').toString(),
            this.$t('sites.propertyCountryEmpty').toString(),
            'error'
          );
          return;
        }
      }
      try {
        const response = await SitesServices.saveSites(this.modifiedSites, this.$i18n.locale);
        if (response.status === 200) {
          // Reload table rows from server to reflect persisted changes
          const table:any = this.$refs.table;
          if (table && table.api && this.url) {
            await table.api.load(this.url);
          }
          // Reset local modification tracking and selection
          this.modifiedSites = [];
          (this as any).selectedRow = [];
          this.sendNotification(
            this.$t('sites.updateSuccess.title').toString(),
            this.$t('sites.updateSuccess.message').toString(),
            'success'
          );
        }
      } catch (err: any) {
        let errorText: string = this.$t('sites.uknownError').toString();
        if (err?.response?.status === 400) {
          const code = err.response.data.errorCode;
          if (code === 'idSapNotValid') errorText = this.$t('sites.idSapNotValid').toString();
          else if (code === 'duplicateIdSap') errorText = this.$t('sites.duplicateIdSap').toString();
          else if (code === 'notEnoughPermission') errorText = this.$t('sites.notEnoughPermission').toString();
          else errorText = this.$t('sites.incorrectValues').toString();
        }
        this.sendNotification(this.$t('sites.updateFailed').toString(), errorText, 'error');
      }
    },
    sendNotification(title: string, text: string, type: string) {
      const isError = type === 'error';
      const notif: any = { group: isError ? 'globalError' : 'global', type, title, text };
      if (isError) notif.duration = Constants.DEFAULT_WAIT_ERROR_NOTIFICATIONS;
      this.$notify(notif);
    },
    loadElements() {
      // placeholder legacy hook
    },
    markModified(site: any) {
      if (!this.modifiedSites.includes(site)) this.modifiedSites.push(site);
    },
    displayCountryText(row:any): string {
      const id = row.site?.country?.id;
      const sel = this.countries.find((c:any)=> c.id === id);
      return sel ? sel.name : '--';
    },
    beginEditCountry(row:any) {
      const key = row.translation?.id || row.site?.id;
      this.$set(this.editingCountry, key, true);
      this.$nextTick(()=>{
        const el:any = (this.$refs as any)[`countrySel-${key}`];
        if (el?.focus) el.focus();
      });
    },
    endEditCountry(row:any) {
      const key = row.translation?.id || row.site?.id;
      this.$set(this.editingCountry, key, false);
    },
    handleCountrySelectChange(row:any) {
      this.markModified(row);
      this.endEditCountry(row);
    },
    duplicateRow(row: any) {
      const tableComp: any = this.$refs.table;
      if (tableComp && tableComp.api) {
        const clone = JSON.parse(JSON.stringify(row));
        clone.translation.id = (clone.translation.id || '') + '-copy';
        tableComp.api.setRows([...tableComp.api.rows, clone]);
        this.markModified(clone);
        const id = clone.site?.id || clone.translation?.id;
        const sel = (this as any).selectedRow; if (id && !sel.includes(id)) sel.push(id);
      }
    },
    toggleEnabled(row: any) {
      row.site.isEnabled = !row.site.isEnabled;
      this.markModified(row);
    },
    onRowClick(row: any) {
      // placeholder for row click pilot
    },
    rowClassFn(row:any) {
      const id = row.site?.id || row.translation?.id;
      return (this as any).selectedRow.includes(id) ? 'selected-row' : '';
    },
  },
  async mounted() {
    this.countries = (await CountryService.getCountriesByAdministrator()).data;
    this.loadElements();
    this.user = JSON.parse(<string>localStorage.getItem('user'));
  },
});
</script>
